import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";
import { X, SwitchCamera, Square, Circle } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { micExclusiveAcquire, micExclusiveRelease } from "../lib/wakeword";

const MAX_SECS = 10 * 60; // 10-minute recording limit (auto-stop)

function fmt(s) {
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${m}:${String(ss).padStart(2, "0")}`;
}

// Best supported container/codec for this browser:
// iOS/Safari records mp4 natively; Chrome/Android use webm (vp9 > vp8).
function pickMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = [
    "video/mp4",
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ];
  for (const c of candidates) {
    try { if (MediaRecorder.isTypeSupported(c)) return c; } catch (e) { /* ignore */ }
  }
  return "";
}

// Best supported audio-only container for the captions track (tiny file for Whisper)
function pickAudioMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
  for (const c of candidates) {
    try { if (MediaRecorder.isTypeSupported(c)) return c; } catch (e) { /* ignore */ }
  }
  return "";
}

/**
 * Fullscreen in-app video recorder.
 * - Live camera preview, front/back flip, MM:SS timer, 10-minute auto-stop.
 * - Camera zoom slider (when the device camera supports the zoom constraint).
 * - Records a parallel low-bitrate audio-only track used for auto captions (Whisper).
 * - Bitrate capped (~2.5Mbps) so a 10-min clip stays well under the 400MB upload limit.
 * - On stop -> onRecorded(blob, mimeType, durationSecs, audioBlob); parent opens the player + uploads.
 */
export function VideoRecorder({ open, onClose, onRecorded }) {
  const { t } = useApp();
  const [facing, setFacing] = useState("environment");
  const [ready, setReady] = useState(false);
  const [recording, setRecording] = useState(false);
  const [secs, setSecs] = useState(0);
  const [zoomCaps, setZoomCaps] = useState(null); // {min, max, step} when camera supports zoom
  const [zoomVal, setZoomVal] = useState(1);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const audioRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioChunksRef = useRef([]);
  const audioDoneRef = useRef(null); // resolve fn of the audio-stop promise
  const timerRef = useRef(null);
  const startedAtRef = useRef(0);
  const cancelledRef = useRef(false);
  const recordingRef = useRef(false);
  recordingRef.current = recording;

  const stopTracks = () => {
    try { streamRef.current?.getTracks().forEach((tr) => tr.stop()); } catch (e) { /* ignore */ }
    streamRef.current = null;
  };

  // Acquire the camera whenever the recorder opens or the facing mode changes
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setReady(false);
    // pause the "Hey Buddy" recognizer BEFORE opening cam+mic (iOS crashes if
    // the audio session is reconfigured under a running recognizer engine)
    const engineWasRunning = micExclusiveAcquire();
    (async () => {
      try {
        if (engineWasRunning && Capacitor.isNativePlatform()) {
          await new Promise((r) => setTimeout(r, 200));
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: facing,
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: true,
        });
        if (!alive) {
          stream.getTracks().forEach((tr) => tr.stop());
          return;
        }
        streamRef.current = stream;
        const v = videoRef.current;
        if (v) {
          v.srcObject = stream;
          try { await v.play(); } catch (e) { /* autoplay quirk — ignore */ }
        }
        // Camera zoom support (mobile cameras): expose a side slider when available
        try {
          const track = stream.getVideoTracks()[0];
          const caps = track && track.getCapabilities ? track.getCapabilities() : {};
          if (caps && caps.zoom && typeof caps.zoom.min === "number" && caps.zoom.max > caps.zoom.min) {
            const settings = track.getSettings ? track.getSettings() : {};
            setZoomCaps({ min: caps.zoom.min, max: caps.zoom.max, step: caps.zoom.step || 0.1 });
            setZoomVal(typeof settings.zoom === "number" ? settings.zoom : caps.zoom.min);
          } else {
            setZoomCaps(null);
          }
        } catch (e) {
          setZoomCaps(null);
        }
        setReady(true);
      } catch (e) {
        toast.error(t("camera_unavailable"));
        onClose && onClose();
      }
    })();
    return () => {
      alive = false;
      try { recorderRef.current?.state !== "inactive" && recorderRef.current?.stop(); } catch (e) { /* ignore */ }
      recorderRef.current = null;
      clearInterval(timerRef.current);
      stopTracks();
      micExclusiveRelease(); // wake listener resumes after a settle delay
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, facing]);

  const startRecording = useCallback(() => {
    const stream = streamRef.current;
    if (!stream || recordingRef.current) return;
    try {
      const mimeType = pickMimeType();
      const mr = new MediaRecorder(stream, {
        ...(mimeType ? { mimeType } : {}),
        videoBitsPerSecond: 2_500_000,
        audioBitsPerSecond: 128_000,
      });
      chunksRef.current = [];
      cancelledRef.current = false;
      // Parallel audio-only recorder (tiny opus track) — used for auto captions
      let audioMr = null;
      let audioPromise = Promise.resolve(null);
      try {
        const audioTracks = stream.getAudioTracks();
        if (audioTracks.length > 0) {
          const aMime = pickAudioMimeType();
          audioMr = new MediaRecorder(new MediaStream(audioTracks), {
            ...(aMime ? { mimeType: aMime } : {}),
            audioBitsPerSecond: 32_000,
          });
          audioChunksRef.current = [];
          audioMr.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) audioChunksRef.current.push(ev.data); };
          audioPromise = new Promise((resolve) => {
            audioDoneRef.current = resolve;
            audioMr.onstop = () => {
              const type = audioMr.mimeType || aMime || "audio/webm";
              resolve(new Blob(audioChunksRef.current, { type }));
              audioChunksRef.current = [];
            };
          });
          audioMr.start(1000);
          audioRecorderRef.current = audioMr;
        }
      } catch (e) { /* captions are best-effort — video still records */ }

      mr.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data); };
      mr.onstop = async () => {
        clearInterval(timerRef.current);
        setRecording(false);
        try { if (audioMr && audioMr.state !== "inactive") audioMr.stop(); } catch (e) { /* ignore */ }
        audioRecorderRef.current = null;
        if (cancelledRef.current) return;
        const duration = (Date.now() - startedAtRef.current) / 1000;
        const type = mr.mimeType || mimeType || "video/webm";
        const blob = new Blob(chunksRef.current, { type });
        chunksRef.current = [];
        if (duration < 1 || blob.size === 0) return; // accidental tap
        // Wait briefly for the captions audio blob (best-effort)
        let audioBlob = null;
        try {
          audioBlob = await Promise.race([audioPromise, new Promise((r) => setTimeout(() => r(null), 2500))]);
        } catch (e) { /* ignore */ }
        onRecorded && onRecorded(blob, type, Math.round(duration), audioBlob);
      };
      recorderRef.current = mr;
      startedAtRef.current = Date.now();
      mr.start(1000); // gather chunks every second (safe for long recordings)
      setSecs(0);
      setRecording(true);
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startedAtRef.current) / 1000);
        setSecs(elapsed);
        if (elapsed >= MAX_SECS) {
          try { mr.stop(); } catch (e) { /* ignore */ }
        }
      }, 500);
    } catch (e) {
      toast.error(t("camera_unavailable"));
    }
  }, [onRecorded, t]);

  const stopRecording = useCallback(() => {
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    } catch (e) { /* ignore */ }
  }, []);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    } catch (e) { /* ignore */ }
    try {
      if (audioRecorderRef.current && audioRecorderRef.current.state !== "inactive") audioRecorderRef.current.stop();
    } catch (e) { /* ignore */ }
    clearInterval(timerRef.current);
    setRecording(false);
    onClose && onClose();
  }, [onClose]);

  // Camera zoom (hardware/digital via track constraint)
  const applyZoom = useCallback((value) => {
    setZoomVal(value);
    try {
      const track = streamRef.current?.getVideoTracks()[0];
      if (track) track.applyConstraints({ advanced: [{ zoom: value }] });
    } catch (e) { /* unsupported mid-stream — ignore */ }
  }, []);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[240] bg-black flex flex-col pt-safe" data-testid="video-recorder">
      {/* Live preview */}
      <video
        ref={videoRef}
        muted
        playsInline
        autoPlay
        className="absolute inset-0 h-full w-full object-cover"
        style={facing === "user" ? { transform: "scaleX(-1)" } : undefined}
        data-testid="video-recorder-preview"
      />

      {/* Top bar: close + timer */}
      <div className="relative z-10 flex items-center justify-between px-4 pt-4">
        <button
          data-testid="video-recorder-close"
          onClick={cancel}
          className="h-11 w-11 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
          aria-label="Close recorder"
        >
          <X className="h-5 w-5" />
        </button>
        <div
          className={`flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-semibold tabular-nums backdrop-blur-sm ${recording ? "bg-red-600/90 text-white" : "bg-black/50 text-white/90"}`}
          data-testid="video-recorder-timer"
        >
          {recording && <span className="h-2 w-2 rounded-full bg-white recording-pulse" />}
          {fmt(secs)} <span className="opacity-70 font-normal">/ {fmt(MAX_SECS)}</span>
        </div>
        <div className="h-11 w-11" />
      </div>

      {/* Camera zoom slider (right side) — only when the camera supports zoom */}
      {zoomCaps && (
        <div
          className="absolute right-2 top-1/2 -translate-y-1/2 z-10 flex flex-col items-center gap-2 rounded-full bg-black/50 backdrop-blur-sm px-1.5 py-3"
          data-testid="video-recorder-zoom"
        >
          <span className="text-white text-[11px] font-semibold tabular-nums" data-testid="video-recorder-zoom-level">
            {Number(zoomVal).toFixed(1)}x
          </span>
          <input
            type="range"
            data-testid="video-recorder-zoom-slider"
            min={zoomCaps.min}
            max={zoomCaps.max}
            step={zoomCaps.step}
            value={zoomVal}
            onChange={(e) => applyZoom(parseFloat(e.target.value))}
            className="accent-white cursor-pointer"
            style={{
              writingMode: "vertical-lr",
              direction: "rtl",
              width: "24px",
              height: "160px",
              touchAction: "none",
            }}
            aria-label="Camera zoom"
          />
        </div>
      )}

      {/* Bottom controls: flip + record/stop */}
      <div className="relative z-10 mt-auto flex items-center justify-center gap-10 pb-10 pt-6">
        <button
          data-testid="video-recorder-flip"
          onClick={() => !recording && setFacing((f) => (f === "environment" ? "user" : "environment"))}
          disabled={recording}
          className={`h-12 w-12 rounded-full bg-black/50 text-white flex items-center justify-center backdrop-blur-sm transition-colors ${recording ? "opacity-40" : "hover:bg-black/70"}`}
          aria-label="Flip camera"
        >
          <SwitchCamera className="h-5 w-5" />
        </button>

        {recording ? (
          <button
            data-testid="video-recorder-stop"
            onClick={stopRecording}
            className="h-[76px] w-[76px] rounded-full border-4 border-white bg-transparent flex items-center justify-center active:scale-95 transition-transform"
            aria-label="Stop recording"
          >
            <Square className="h-8 w-8 text-red-500 fill-red-500 rounded-sm" />
          </button>
        ) : (
          <button
            data-testid="video-recorder-start"
            onClick={startRecording}
            disabled={!ready}
            className={`h-[76px] w-[76px] rounded-full border-4 border-white bg-transparent flex items-center justify-center active:scale-95 transition-transform ${!ready ? "opacity-50" : ""}`}
            aria-label="Start recording"
          >
            <Circle className="h-14 w-14 text-red-500 fill-red-500" />
          </button>
        )}

        <div className="h-12 w-12" />
      </div>

      {!ready && (
        <div className="absolute inset-0 z-[5] flex items-center justify-center">
          <span className="text-white/80 text-sm" data-testid="video-recorder-loading">{t("loading")}...</span>
        </div>
      )}
    </div>,
    document.body
  );
}
